import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Select, Card, Statistic, Spin } from "antd";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { fromLonLat } from "ol/proj";
import { Polygon as OLPolygon } from "ol/geom";
import Feature from "ol/Feature";
import Overlay from "ol/Overlay";
import { Fill, Stroke, Style } from "ol/style";
import { isEqual } from "lodash";

import { COLORS } from "../../../../consts";
import { LoadingIcon } from "../../../../components";
import MapNavigator from "./MapNavigator.jsx";

/* ---------- tiny helpers ---------- */
const validateOrRepairLatLng = (lat, lng) => {
  const latF = +lat,
    lngF = +lng;
  if (latF >= -90 && latF <= 90 && lngF >= -180 && lngF <= 180)
    return [latF, lngF];
  if (lngF >= -90 && lngF <= 90 && latF >= -180 && latF <= 180)
    return [lngF, latF];
  return null;
};
const centroid = (pts) =>
  pts
    .reduce((s, p) => [s[0] + p[0], s[1] + p[1]], [0, 0])
    .map((v) => v / pts.length);
const leftMost = (pts) => {
  let minLng = Infinity;
  let chosen = null;
  for (const [lat, lng] of pts) {
    if (lng < minLng) {
      minLng = lng;
      chosen = [lat - 0.00001, lng + 0.0003];
    }
  }
  return chosen;
};
const rightMost = (pts) => {
  let maxLng = -Infinity;
  let chosen = null;
  for (const [lat, lng] of pts) {
    if (lng > maxLng) {
      maxLng = lng;
      chosen = [lat - 0.00004, lng - 0.0001];
    }
  }
  return chosen;
};
/**
 * Smallest convex outline enclosing every row of a greenhouse (monotone chain).
 * The rows of one greenhouse can sit in several separated groups — a planting
 * scanned as multiple bays — so joining their points in row order would trace a
 * zig-zag that jumps between groups instead of an outline around them.
 * Points are [lat, lng]; treated as planar, which is exact enough at field scale.
 */
const convexHull = (pts) => {
  if (pts.length < 3) return pts;
  const sorted = [...pts].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  const cross = (o, a, b) =>
    (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
  const build = (src) => {
    const out = [];
    for (const p of src) {
      while (
        out.length >= 2 &&
        cross(out[out.length - 2], out[out.length - 1], p) <= 0
      ) {
        out.pop();
      }
      out.push(p);
    }
    out.pop();
    return out;
  };
  const hull = [...build(sorted), ...build([...sorted].reverse())];
  return hull.length >= 3 ? hull : pts;
};
const buildColourMap = (typeMap, cfg) => {
  const out = {};
  Object.entries(typeMap).forEach(([t, bays]) =>
    bays.forEach((b) => (out[b] = cfg?.[t]?.color || null)),
  );
  return out;
};

/**
 * GreenhouseMapModal
 * - Renders a modal with a map of greenhouses and rows.
 * - Includes a multi-select dropdown for greenhouse "types" with color labels.
 * - By default, only type 1 is selected.
 */
function GreenhouseMapModal({
  visible,
  onClose,
  greenhouseMapTileUrl = "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  greenhouseCoordinates = {},
  typeToGreenhouseMap = {},
  greenhouseGroupConfig = {},
  cards = null,
  greenhouseLabel = "Bay",
  blockLabel = "Row",
}) {
  /* ---------- local state ---------- */
  const [selectedTypes, setSelectedTypes] = useState([1, 2]);
  const [mapReady, setMapReady] = useState(false);
  const dataLoaded =
    !!greenhouseCoordinates &&
    Object.keys(greenhouseCoordinates).length > 0 &&
    !!typeToGreenhouseMap &&
    Object.keys(typeToGreenhouseMap).length > 0;

  /* ---------- derive bay list / colours ---------- */
  const colourMap = useMemo(
    () => buildColourMap(typeToGreenhouseMap, greenhouseGroupConfig),
    [typeToGreenhouseMap, greenhouseGroupConfig],
  );

  const visibleBays = useMemo(() => {
    const arr = [];
    selectedTypes.forEach((t) => arr.push(...(typeToGreenhouseMap[t] || [])));
    return Array.from(new Set(arr));
  }, [selectedTypes, typeToGreenhouseMap]);

  /* ---------- build polygons ---------- */
  const { greenhousePolys, rowPolys } = useMemo(() => {
    if (!dataLoaded) return { greenhousePolys: [], rowPolys: [] };
    const gh = [],
      rows = [],
      palette = COLORS.graphColors;
    Object.entries(greenhouseCoordinates)
      .filter(([name]) => visibleBays.includes(name))
      .forEach(([bay, rowDict], idx) => {
        const colour = colourMap[bay] || palette[idx % palette.length];
        const rowFeats = Object.entries(rowDict).flatMap(([rName, pts]) => {
          const repaired = pts
            .map(([la, ln]) => validateOrRepairLatLng(la, ln))
            .filter(Boolean);
          return repaired.length >= 3
            ? [
                {
                  greenhouseName: bay,
                  rowName: rName,
                  color: colour,
                  positions: repaired,
                },
              ]
            : [];
        });
        if (rowFeats.length) {
          rows.push(...rowFeats);
          gh.push({
            greenhouseName: bay,
            color: colour,
            positions: convexHull(rowFeats.flatMap((r) => r.positions)),
          });
        }
      });
    return { greenhousePolys: gh, rowPolys: rows };
  }, [greenhouseCoordinates, visibleBays, colourMap, dataLoaded]);

  /* keep a deterministic order (for label placement) */
  const sortedGH = useMemo(() => {
    const num = greenhousePolys.every((g) => !isNaN(+g.greenhouseName));
    return [...greenhousePolys].sort((a, b) =>
      num
        ? +a.greenhouseName - +b.greenhouseName
        : a.greenhouseName.localeCompare(b.greenhouseName),
    );
  }, [greenhousePolys]);
  const labelAnchorCycle = useMemo(
    () => sortedGH.map((_, i) => ["left", "left", "left"][i % 3]),
    [sortedGH],
  );

  /* ---------- OpenLayers refs ---------- */
  const mapDiv = useRef(null);
  const mapRef = useRef(null);
  const vectorLayerRef = useRef(null);
  const ghOverlays = useRef([]);

  /* ---------- build / destroy map ---------- */
  useEffect(() => {
    if (!visible || !dataLoaded || mapRef.current) return;
    vectorLayerRef.current = new VectorLayer({ source: new VectorSource() });
    mapRef.current = new Map({
      target: mapDiv.current,
      layers: [
        new TileLayer({
          source: new XYZ({ url: greenhouseMapTileUrl, maxZoom: 20 }),
        }),
        vectorLayerRef.current,
      ],
      view: new View({ center: [0, 0], zoom: 2 }),
      controls: [],
    });
    setTimeout(() => mapRef.current?.updateSize(), 0);
    return () => {
      mapRef.current?.setTarget(null);
      mapRef.current = null;
    };
  }, [visible, greenhouseMapTileUrl, dataLoaded]);

  /* ---------- render polygons whenever data ready ---------- */
  useEffect(() => {
    if (!mapRef.current) return;
    // It's okay if sortedGH.length === 0 and rowPolys.length === 0
    setMapReady(false);
    const map = mapRef.current;
    const vSrc = vectorLayerRef.current.getSource();
    vSrc.clear();
    ghOverlays.current.forEach((ov) => map.removeOverlay(ov));
    ghOverlays.current = [];

    /* --- greenhouse outlines & permanent labels --- */
    sortedGH.forEach((gh, idx) => {
      const coords = gh.positions.map(([la, ln]) => fromLonLat([ln, la]));
      const feat = new Feature({ geometry: new OLPolygon([coords]) });
      feat.setStyle(
        new Style({
          stroke: new Stroke({ color: gh.color, width: 2 }),
          fill: new Fill({ color: "rgba(0,0,0,0)" }),
        }),
      );
      vSrc.addFeature(feat);

      const anchor =
        labelAnchorCycle[idx] === "left"
          ? leftMost(gh.positions)
          : labelAnchorCycle[idx] === "right"
            ? rightMost(gh.positions)
            : centroid(gh.positions);

      const div = document.createElement("div");
      div.style.cssText = `background:#fff;border-radius:4px;font-weight:bold;padding:0 4px;white-space:nowrap;color:${gh.color}`;
      div.textContent = `${greenhouseLabel} ${gh.greenhouseName}`;
      const ov = new Overlay({
        element: div,
        positioning: "bottom-center",
        offset: [0, -10],
      });
      ov.setPosition(fromLonLat([anchor[1], anchor[0]]));
      map.addOverlay(ov);
      ghOverlays.current.push(ov);
    });

    /* --- row outlines --- */
    rowPolys.forEach((row) => {
      const coords = row.positions.map(([la, ln]) => fromLonLat([ln, la]));
      const feat = new Feature({ geometry: new OLPolygon([coords]) });
      feat.setStyle(
        new Style({
          stroke: new Stroke({ color: row.color, width: 2 }),
          fill: new Fill({ color: "rgba(0,0,0,0)" }),
        }),
      );
      feat.set("row", row); // store for tooltip
      vSrc.addFeature(feat);
    });

    /* --- hover tooltip for rows --- */
    const tip = document.createElement("div");
    tip.style.cssText =
      "position:absolute;background:#fff;border-radius:4px;font-weight:bold;padding:0 4px;white-space:nowrap;pointer-events:none;transform:translate(-50%,-100%);";
    const tipOv = new Overlay({
      element: tip,
      positioning: "bottom-center",
      offset: [0, -10],
    });
    map.addOverlay(tipOv);
    const move = (e) => {
      if (e.dragging) return tipOv.setPosition(undefined);
      const f = map.forEachFeatureAtPixel(e.pixel, (ft) => ft);
      if (f && f.get("row")) {
        const { greenhouseName, rowName, color } = f.get("row");
        tip.innerHTML = `<span style="color:${color}">${greenhouseLabel} ${greenhouseName} : ${rowName}</span>`;
        tipOv.setPosition(e.coordinate);
      } else tipOv.setPosition(undefined);
    };
    map.on("pointermove", move);

    /* --- fit view instantly --- */
    const allPts = [
      ...greenhousePolys.flatMap((g) => g.positions),
      ...rowPolys.flatMap((r) => r.positions),
    ];
    if (allPts.length) {
      const ext = new OLPolygon([
        allPts.map(([la, ln]) => fromLonLat([ln, la])),
      ]).getExtent();
      map
        .getView()
        .fit(ext, { padding: [50, 50, 50, 50], maxZoom: 22, duration: 0 });
    }
    setTimeout(() => map.updateSize(), 0); // final resize
    setMapReady(true); // done – hide spinner

    return () => {
      map.un("pointermove", move);
      map.removeOverlay(tipOv);
    };
  }, [sortedGH, rowPolys, labelAnchorCycle]);

  /* ---------- dropdown options ---------- */
  const typeOptions = useMemo(
    () =>
      Object.entries(greenhouseGroupConfig).map(([k, { label, color }]) => ({
        value: +k,
        label: (
          <span style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                marginRight: 8,
                background: color,
              }}
            />
            {label}
          </span>
        ),
      })),
    [greenhouseGroupConfig],
  );

  /* ---------- render ---------- */
  return (
    <Modal
      open={visible}
      footer={null}
      maskClosable={false}
      onCancel={onClose}
      destroyOnHidden={true}
      centered
      width="90vw"
      bodyStyle={{ height: "88vh", padding: 0 }}
    >
      {/* top bar */}
      <div
        style={{ display: "flex", justifyContent: "space-between", padding: 8 }}
      >
        <Select
          mode="multiple"
          options={typeOptions}
          style={{ minWidth: 300 }}
          value={selectedTypes}
          onChange={setSelectedTypes}
          placeholder="Select greenhouse types"
        />
      </div>

      {/* cards */}
      {cards && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: 8 }}>
          {Object.values(cards).map((c) => (
            <Card
              key={c.title}
              variant="outlined"
              style={{ flex: "1 1 240px", minWidth: 240 }}
            >
              <Statistic
                title={
                  <>
                    {c.title} (
                    <span style={{ color: c.color, fontWeight: "bold" }}>
                      {c.count} {greenhouseLabel}s
                    </span>
                    )
                  </>
                }
                styles={{ content: { color: c.color } }}
                valueRender={() => c.content}
              />
            </Card>
          ))}
        </div>
      )}

      {/* map area */}
      <div style={{ height: "calc(67%)", width: "100%", position: "relative" }}>
        <div
          ref={mapDiv}
          style={{
            height: "100%",
            width: "100%",
            visibility: dataLoaded ? "visible" : "hidden",
          }}
        />

        {!dataLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-30 bg-white/75">
            <Spin indicator={<LoadingIcon height="180px" />} />
          </div>
        )}

        {dataLoaded &&
          mapReady &&
          sortedGH.length === 0 &&
          rowPolys.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-white/85 text-lg">
              No valid {greenhouseLabel} / {blockLabel}s to display!
            </div>
          )}

        <MapNavigator map={mapRef.current} />
      </div>
    </Modal>
  );
}

export default React.memo(GreenhouseMapModal, (p, n) =>
  [
    "visible",
    "greenhouseCoordinates",
    "greenhouseMapTileUrl",
    "typeToGreenhouseMap",
    "greenhouseGroupConfig",
    "cards",
    "greenhouseLabel",
    "blockLabel",
  ].every((k) => isEqual(p[k], n[k])),
);
